export type AccountMode = "cross" | "isolated";

// Per-market leverage storage (e.g., {"SOL/USD": 5, "BTC/USD": 10})
export type MarketLeverages = Record<string, number>;

export interface TradingSettings {
  // NOTE: accountMode is NOT stored in localStorage - it comes from the backend via Zustand store
  // This ensures the backend is the single source of truth for margin mode
  marketLeverages: MarketLeverages;
  slippageTolerance: number | null; // null = Auto (no limit), number = specific %
  /** Global default leverage for markets without a per-market override.
   *  Industry standard: applies to any market the user hasn't explicitly customized.
   *  Per-market overrides (in marketLeverages) always take priority. */
  globalDefaultLeverage: number;
}

// Leverage tier from API
export interface LeverageTier {
  tier_min_leverage: number;
  tier_max_leverage: number;
  max_position: string;
  enabled: boolean;
}

export interface LeverageConfig {
  market: string;
  max_leverage: number;
  tiers: LeverageTier[];
}

// Cache for leverage configs
const leverageConfigCache: Map<string, { config: LeverageConfig; timestamp: number }> = new Map();
const CACHE_TTL = 60_000; // 1 minute cache

// Import centralized config — avoids inconsistent env var reads
import { API_BASE_URL } from "@/backend/config";

/**
 * Fetch leverage config from API for a specific market
 */
export async function fetchLeverageConfig(market: string): Promise<LeverageConfig | null> {
  // Check cache
  const cached = leverageConfigCache.get(market);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.config;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/leverage-config/${encodeURIComponent(market)}`);
    if (!response.ok) {
      console.warn(`Failed to fetch leverage config for ${market}: ${response.statusText}`);
      return null;
    }
    const config: LeverageConfig = await response.json();
    
    // Update cache
    leverageConfigCache.set(market, { config, timestamp: Date.now() });
    
    return config;
  } catch (error) {
    console.warn(`Error fetching leverage config for ${market}:`, error);
    return null;
  }
}

/**
 * Get max position for a specific leverage and market from cached config
 * Falls back to hardcoded defaults if API config is not available
 */
export function getMaxPositionFromConfig(leverage: number, config: LeverageConfig | null): number {
  if (config && config.tiers.length > 0) {
    for (const tier of config.tiers) {
      if (leverage >= tier.tier_min_leverage && leverage <= tier.tier_max_leverage && tier.enabled) {
        return parseFloat(tier.max_position);
      }
    }
  }
  
  // Fallback to hardcoded defaults
  return getMaxPositionForLeverage(leverage);
}

/**
 * Calculate max position based on leverage (risk management tiers)
 * Higher leverage = lower max position (like Binance/Bybit)
 * 
 * This is the fallback/default tier structure:
 * - 1-5x leverage: $10M max position
 * - 6-10x leverage: $5M max position
 * - 11-20x leverage: $2.5M max position
 * - 21-50x leverage: $1M max position
 * - 51-75x leverage: $500K max position
 * - 76-100x leverage: $250K max position
 */
export const getMaxPositionForLeverage = (leverage: number): number => {
  if (leverage <= 5) return 10_000_000;     // 1-5x: $10M max
  if (leverage <= 10) return 5_000_000;     // 6-10x: $5M max
  if (leverage <= 20) return 2_500_000;     // 11-20x: $2.5M max
  if (leverage <= 50) return 1_000_000;     // 21-50x: $1M max
  if (leverage <= 75) return 500_000;       // 51-75x: $500K max
  return 250_000;                            // 76-100x: $250K max
};

